import { A } from '@solidjs/router';
import { type Component, createResource, createSignal, For, Show } from 'solid-js';
import {
  type BookmarkedProject,
  fetchBookmarkedProjects,
  removeProjectBookmark,
} from '../services/api';

/**
 * BookmarkedProjects Page
 *
 * Shows projects that the user has bookmarked from the marketplace.
 * These are view-only references to other users' projects.
 */
const BookmarkedProjects: Component = () => {
  const [page, setPage] = createSignal(0);
  const limit = 12;

  const [bookmarks, { refetch }] = createResource(
    () => ({ offset: page() * limit, limit }),
    fetchBookmarkedProjects,
  );

  const [removingId, setRemovingId] = createSignal<string | null>(null);

  const handleRemoveBookmark = async (projectId: string) => {
    setRemovingId(projectId);
    try {
      await removeProjectBookmark(projectId);
      refetch();
    } catch (error) {
      console.error('Failed to remove bookmark:', error);
    } finally {
      setRemovingId(null);
    }
  };

  const totalPages = () => Math.ceil((bookmarks()?.total || 0) / limit);

  return (
    <div>
      {/* Page Header */}
      <div class="mb-6">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-2xl font-semibold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
              Bookmarked Projects
            </h1>
            <p class="text-gray-400 text-sm mt-1">
              Projects you've saved from the marketplace for quick access
            </p>
          </div>
          <A
            href="/marketplace"
            class="inline-flex items-center px-4 py-2 rounded-lg bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white font-medium transition-all"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="mr-2"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            Browse Marketplace
          </A>
        </div>
      </div>

      {/* Loading State */}
      <Show when={bookmarks.loading}>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <For each={[1, 2, 3, 4, 5, 6]}>
            {() => (
              <div class="bg-gray-800/50 rounded-lg border border-gray-700 p-6 animate-pulse">
                <div class="h-6 bg-gray-700 rounded w-3/4 mb-4" />
                <div class="h-4 bg-gray-700 rounded w-full mb-2" />
                <div class="h-4 bg-gray-700 rounded w-2/3" />
              </div>
            )}
          </For>
        </div>
      </Show>

      {/* Error State */}
      <Show when={bookmarks.error}>
        <div class="bg-red-900/20 border border-red-700 rounded-lg p-6 text-center">
          <p class="text-red-400">Failed to load bookmarks. Please try again.</p>
          <button
            onClick={() => refetch()}
            class="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white"
          >
            Retry
          </button>
        </div>
      </Show>

      {/* Empty State */}
      <Show when={!bookmarks.loading && !bookmarks.error && bookmarks()?.items.length === 0}>
        <div class="bg-gray-800/50 rounded-lg border border-gray-700 p-12 text-center">
          <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-yellow-500/10 mb-6">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="text-yellow-400"
            >
              <path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z" />
            </svg>
          </div>
          <h2 class="text-xl font-medium text-gray-200 mb-2">No Bookmarked Projects</h2>
          <p class="text-gray-400 mb-6 max-w-md mx-auto">
            You haven't bookmarked any projects yet. Browse the marketplace to find interesting
            projects and save them here for quick access.
          </p>
          <A
            href="/marketplace"
            class="inline-flex items-center px-4 py-2 rounded-lg bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white font-medium transition-all"
          >
            Explore Marketplace
          </A>
        </div>
      </Show>

      {/* Projects Grid */}
      <Show when={!bookmarks.loading && !bookmarks.error && (bookmarks()?.items.length || 0) > 0}>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <For each={bookmarks()?.items}>
            {(project: BookmarkedProject) => (
              <div class="bg-gray-800/50 rounded-lg border border-gray-700 hover:border-gray-600 transition-colors overflow-hidden">
                <div class="p-6">
                  <div class="flex items-start justify-between mb-3">
                    <h3 class="font-medium text-gray-200 truncate flex-1">{project.name}</h3>
                    <button
                      onClick={() => handleRemoveBookmark(project.id)}
                      disabled={removingId() === project.id}
                      class="ml-2 p-1 text-gray-400 hover:text-red-400 transition-colors disabled:opacity-50"
                      title="Remove bookmark"
                    >
                      <Show
                        when={removingId() !== project.id}
                        fallback={
                          <svg
                            class="animate-spin h-5 w-5"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              class="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              stroke-width="4"
                            />
                            <path
                              class="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                        }
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        >
                          <path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z" />
                        </svg>
                      </Show>
                    </button>
                  </div>
                  <p class="text-sm text-gray-400 line-clamp-2 mb-4">
                    {project.goal || project.description || 'No description'}
                  </p>
                  <div class="flex items-center gap-2 text-xs text-gray-500 mb-4">
                    <span class="px-2 py-1 bg-gray-700/50 rounded">{project.status}</span>
                    <Show when={project.rating}>
                      <span class="flex items-center gap-1">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          class="text-yellow-400"
                        >
                          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                        </svg>
                        {project.rating?.toFixed(1)}
                      </span>
                    </Show>
                  </div>
                  <div class="flex items-center justify-between">
                    <span class="text-xs text-gray-500">
                      Saved {new Date(project.bookmarkedAt).toLocaleDateString()}
                    </span>
                    <A
                      href={`/projects/${project.id}`}
                      class="text-sm text-purple-400 hover:text-purple-300 transition-colors"
                    >
                      View Project
                    </A>
                  </div>
                </div>
              </div>
            )}
          </For>
        </div>

        {/* Pagination */}
        <Show when={totalPages() > 1}>
          <div class="flex items-center justify-center gap-2 mt-8">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page() === 0}
              class="px-3 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span class="text-gray-400 px-4">
              Page {page() + 1} of {totalPages()}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages() - 1, p + 1))}
              disabled={page() >= totalPages() - 1}
              class="px-3 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </Show>
      </Show>

      {/* Feature Info */}
      <div class="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
        <div class="bg-gray-800/30 rounded-lg border border-gray-700/50 p-6">
          <div class="text-purple-400 mb-3">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z" />
            </svg>
          </div>
          <h3 class="font-medium text-gray-200 mb-2">Quick Save</h3>
          <p class="text-sm text-gray-400">
            Save projects you find interesting with a single click for quick access later.
          </p>
        </div>

        <div class="bg-gray-800/30 rounded-lg border border-gray-700/50 p-6">
          <div class="text-blue-400 mb-3">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          </div>
          <h3 class="font-medium text-gray-200 mb-2">View Only</h3>
          <p class="text-sm text-gray-400">
            Bookmarked projects are read-only. Join or fork them to make changes.
          </p>
        </div>

        <div class="bg-gray-800/30 rounded-lg border border-gray-700/50 p-6">
          <div class="text-green-400 mb-3">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
              <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
            </svg>
          </div>
          <h3 class="font-medium text-gray-200 mb-2">Stay Updated</h3>
          <p class="text-sm text-gray-400">
            Get notified when bookmarked projects are updated or have new versions.
          </p>
        </div>
      </div>
    </div>
  );
};

export default BookmarkedProjects;
